import { useEffect, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Download, FileSpreadsheet, TrendingUp, Wallet, Users, Briefcase } from "lucide-react";
import { STORAGE_KEYS } from "@/lib/storage";

const AXIS_TICK = { fill: "#57534E", fontSize: 12 };
const AXIS_LINE = { stroke: "#E7E5E4" };
const TOOLTIP_STYLE = { background: "#fff", border: "1px solid #E7E5E4", borderRadius: 0, fontSize: 12 };

const startOfMonth = () => {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
};
const endOfMonth = () => {
  const d = new Date(); d.setMonth(d.getMonth() + 1, 0);
  return d.toISOString().slice(0, 10);
};

const PAYMENT_LABELS = {
  contanti: "Contanti", carta: "Carta", pos: "POS", bonifico: "Bonifico",
  booking_com: "Booking.com", airbnb: "Airbnb", expedia: "Expedia",
  voucher: "Voucher", altro: "Altro", non_specificato: "Non specificato",
};
const STATUS_LABELS = {
  non_pagato: "Non pagato", pagato: "Pagato",
  parziale: "Parzialmente pagato", rimborsato: "Rimborsato",
};

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [from, setFrom] = useState(startOfMonth());
  const [to, setTo] = useState(endOfMonth());
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    api.get("/stats/overview").then(r => setStats(r.data)).catch(() => {});
    api.get("/stats/revenue").then(r => setRevenue(r.data)).catch(() => {});
  }, []);

  const loadSummary = useCallback(async () => {
    if (!from || !to) return;
    setLoadingSummary(true);
    try {
      const { data } = await api.get(`/stats/sales-summary?date_from=${from}&date_to=${to}`);
      setSummary(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setLoadingSummary(false); }
  }, [from, to]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const onExport = async () => {
    try {
      const propertyId = localStorage.getItem(STORAGE_KEYS.propertyId);
      const res = await fetch(`${API}/stats/sales-export?date_from=${from}&date_to=${to}`, {
        credentials: "include",
        headers: propertyId ? { "X-Property-Id": propertyId } : {},
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `vendite_${from}_${to}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV vendite scaricato");
    } catch { toast.error("Errore download"); }
  };

  return (
    <div className="p-6 md:p-12 max-w-7xl">
      <p className="text-xs tracked text-blue-700 mb-2">REPORT</p>
      <h1 className="font-heading font-semibold text-4xl tracking-tight mb-2" data-testid="reports-title">Statistiche e vendite</h1>
      <p className="text-slate-600 mb-8 text-sm">
        Riepilogo di quanto hai venduto. Usa questi dati per emettere fattura sul tuo gestionale di fatturazione (es. Fatture in Cloud, Aruba, ecc.).
      </p>

      {/* KPI generali */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
        <KPI label="Camere" value={stats?.rooms_total ?? 0} />
        <KPI label="Ospiti registrati" value={stats?.guests_total ?? 0} />
        <KPI label="Prenotazioni totali" value={stats?.bookings_total ?? 0} />
        <KPI label="Occupazione oggi" value={`${stats?.occupancy_today ?? 0}%`} />
        <KPI label="ADR (prezzo medio/notte)" value={`€ ${stats?.adr ?? 0}`} />
        <KPI label="RevPAR (ricavo/camera)" value={`€ ${stats?.revpar ?? 0}`} />
      </div>

      {/* === RIEPILOGO VENDITE PER FATTURAZIONE === */}
      <div className="border-2 border-blue-200 bg-blue-50 p-6 mb-8" data-testid="sales-summary-card">
        <div className="flex items-center gap-2 mb-2">
          <Wallet className="w-5 h-5 text-blue-700" strokeWidth={1.5} />
          <h2 className="font-heading font-semibold text-2xl tracking-tight">Riepilogo vendite per periodo</h2>
        </div>
        <p className="text-sm text-slate-700 mb-5">
          Seleziona il periodo (di solito un mese) per vedere quanto hai incassato. Esporta il CSV e portalo al commercialista o usalo sul tuo portale di fatturazione.
        </p>

        <div className="grid sm:grid-cols-3 gap-3 mb-5">
          <label className="block">
            <span className="text-xs text-slate-500 mb-1 block">Da</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              data-testid="sales-from"
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-700 bg-white" />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 mb-1 block">A</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              data-testid="sales-to"
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-700 bg-white" />
          </label>
          <div className="flex items-end gap-2">
            <button onClick={loadSummary} disabled={loadingSummary} data-testid="sales-load"
              className="bg-stone-900 text-white px-4 py-2 text-sm rounded-lg hover:bg-stone-800 disabled:opacity-60">
              {loadingSummary ? "..." : "Calcola"}
            </button>
            <button onClick={onExport} data-testid="sales-export"
              className="inline-flex items-center gap-2 border-2 border-slate-300 bg-white text-slate-900 px-4 py-2 text-sm rounded-lg hover:bg-slate-50">
              <Download className="w-4 h-4" /> Esporta CSV
            </button>
          </div>
        </div>

        {summary && (
          <>
            {/* Totali grandi */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <BigCell label="Totale fatturabile" value={`€ ${summary.totals.revenue.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`} highlight />
              <BigCell label="Già incassato" value={`€ ${summary.totals.paid.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`} />
              <BigCell label="Ancora da incassare" value={`€ ${summary.totals.outstanding.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`} warn={summary.totals.outstanding > 0} />
              <BigCell label="Notti vendute" value={summary.totals.nights} />
            </div>

            {/* Tipo cliente */}
            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              <SegmentCell icon={Users} label="Privati" count={summary.totals.private_count} />
              <SegmentCell icon={Briefcase} label="Aziende" count={summary.totals.company_count} amount={summary.totals.company_revenue} />
            </div>

            {/* Per metodo pagamento */}
            <div className="grid md:grid-cols-2 gap-4">
              <Breakdown title="Per metodo di pagamento" rows={summary.by_payment_method} labels={PAYMENT_LABELS} testid="break-method" />
              <Breakdown title="Per stato pagamento" rows={summary.by_payment_status} labels={STATUS_LABELS} testid="break-status" />
            </div>
          </>
        )}
      </div>

      {/* Grafico ricavi storici */}
      <div className="border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-blue-700" strokeWidth={1.5} />
          <h2 className="font-heading font-semibold text-2xl tracking-tight">Andamento ricavi · ultimi 6 mesi</h2>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={revenue}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
            <XAxis dataKey="month" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} />
            <YAxis tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Bar dataKey="revenue" fill="#9A3412" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Nota */}
      <div className="mt-6 p-4 border border-slate-200 bg-slate-50 text-sm text-slate-600">
        <FileSpreadsheet className="w-4 h-4 inline-block mr-2 text-slate-500" strokeWidth={1.5} />
        Questi numeri sono solo dati gestionali. Per emettere fattura usa il tuo portale di fatturazione elettronica abituale (Fatture in Cloud, Aruba, ecc.). Il CSV scaricato include intestatari, P.IVA, codici SDI delle aziende e dettaglio prenotazioni — pronto per essere consegnato al commercialista.
      </div>
    </div>
  );
}

function KPI({ label, value }) {
  return (
    <div className="border border-slate-200 bg-white p-5">
      <div className="text-xs tracked text-slate-500">{label.toUpperCase()}</div>
      <div className="font-heading font-semibold text-3xl tracking-tight mt-2">{value}</div>
    </div>
  );
}

function BigCell({ label, value, highlight, warn }) {
  let cls = "border-2 border-slate-200 bg-white";
  if (highlight) cls = "border-2 border-sky-300 bg-white";
  if (warn) cls = "border-2 border-amber-300 bg-amber-50";
  return (
    <div className={`${cls} p-5`}>
      <div className="text-xs tracked text-slate-500">{label.toUpperCase()}</div>
      <div className="font-heading font-semibold text-2xl tracking-tight mt-2">{value}</div>
    </div>
  );
}

function SegmentCell({ icon: Icon, label, count, amount }) {
  return (
    <div className="flex items-center gap-4 border border-slate-200 bg-white p-4">
      <Icon className="w-6 h-6 text-slate-500" strokeWidth={1.5} />
      <div>
        <div className="text-xs tracked text-slate-500">{label.toUpperCase()}</div>
        <div className="font-heading font-semibold text-xl">
          {count} prenotazion{count === 1 ? "e" : "i"}
          {typeof amount === "number" && <span className="text-slate-500 text-sm font-sans ml-2">· € {amount.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</span>}
        </div>
      </div>
    </div>
  );
}

function Breakdown({ title, rows, labels, testid }) {
  const entries = Object.entries(rows || {}).sort((a, b) => b[1].amount - a[1].amount);
  return (
    <div className="border border-slate-200 bg-white p-4" data-testid={testid}>
      <h3 className="text-xs tracked text-slate-500 mb-3">{title.toUpperCase()}</h3>
      {entries.length === 0 ? (
        <p className="text-sm text-slate-500">Nessun dato.</p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {entries.map(([k, v]) => (
              <tr key={k} className="border-b border-stone-100 last:border-0">
                <td className="py-2 text-slate-700">{labels[k] || k}</td>
                <td className="py-2 text-right text-slate-500 text-xs">{v.count}×</td>
                <td className="py-2 text-right font-medium">€ {v.amount.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
